import React, { useEffect, useState } from 'react';
import { Box, Dialog, Grid, Typography } from '@mui/material';
import CustomButton from '../../../../common/components/buttons/CustomButton';
import CustomDataGrid from '../../../../common/components/customized/customdatagrid/CustomDataGrid';
import './Maintenance.scss';
import AddMaintenance from './AddMaintenance';
import CustomDeletePopup from '../../../../common/components/customdelete/CustomDeletePopup';
import CustomSelect from '../../../../common/components/customized/customselect/CustomSelect';
import CustomDateTimeCalendar from '../../../../common/components/customized/customdatetimecalendar/CustomDateTimeCalendar';
import dayjs from 'dayjs';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  capitalizeFirstLetter,
  convertToEpoch,
  convertToEpoch2,
  useAbort
} from '../../../../utils/commonFunctions';
import { updateToast } from '../../../../common/redux/reducer/commonSlices/toastSlice';
import { useAppDispatch, useAppSelector } from '../../../../app/redux/hooks';
import {
  clearData,
  getMaintenance,
  truckNumbers
} from '../../redux/reducer/truckManagementSlices/maintenanceSlice';
import { getPDFExcel } from '../../../yaantrac/redux/reducer/yaantracSlices/reportSlice';
import CustomTextField from '../../../../common/components/customized/customtextfield/CustomTextField';

const validationSchema = yup.object().shape({
  vehicle: yup.string().required('Vehicle Number is required').nullable(),
  startDate: yup
    .date()
    .required('Start Date is required')
    .nullable()
    .typeError('Start Date is invalid'),
  endDate: yup
    .date()
    .required('End Date is required')
    .nullable()
    .typeError('End Date is invalid')
});

const mailSchema = yup.object().shape({
  email: yup.string().email('Enter valid Email-ID').required('Email-ID is required'),

  document: yup.string().required('Document Type is required').nullable()
});

interface ReportData {
  startDate: Date | null;
  endDate: Date | null;
  vehicle: string | null;
}

export interface PaginationModel {
  pageNo: number;
  pageSize: number;
}

const Maintenance = () => {
  const [role, setRole] = useState<string>('Add Maintenance');
  const [open, setOpen] = useState<boolean>(false);
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [filteredRow, setFilteredRow] = useState<null>();
  const [message, setMessage] = useState<string>('');
  const [isDateDisabled, setIsDateDisabled] = useState<boolean>(false);
  const [toDateDisabled, setToDateDisabled] = useState<boolean>(true);
  const [getStartDate, setGetStartDate] = useState<Date | null>(null);
  const [getEndDate, setGetEndDate] = useState<Date | null>(null);
  const [fromTimestamp, setFromTimeStamp] = useState<number | null>(null);
  const [currentTimestamp, setCurrentTimeStamp] = useState<number | null>(null);
  const [toTimeStamp, setToTimeStamp] = useState<number | null>(null);
  const [MaintenanceRows, setMaintenanceRows] = useState([]);
  const [isDeviceDisabled, setIsDeviceDisabled] = useState<boolean>(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [showDataGrid, setShowDataGrid] = useState<boolean>(false);
  const [sortModel, setSortModel] = useState<any>([]);
  const [payload, setPayload] = useState<any>();
  const [drawer, setDrawer] = useState(false);
  const [newMail, setNewMail] = useState(null);

  const { data, isLoading: maintenanceLoading } = useAppSelector(
    state => state.truckNumbers
  );
  const {
    data: maintenanceData,
    isLoading: loading,
    count
  } = useAppSelector(state => state.getMaintenance);
  const { data: pdfData, isLoading: buttonLoading } = useAppSelector(
    state => state.pdfExcel
  );
  const { email } = useAppSelector(state => state.myProfile.data || { email: '' });
  const dispatch = useAppDispatch();
  const createAbort = useAbort();

  const { control, handleSubmit, setValue, clearErrors, reset } = useForm({
    resolver: yupResolver(validationSchema)
  });

  const {
    control: mailControl,
    handleSubmit: mailSubmit,
    getValues: getMailValues,
    setValue: setMailValues,
    clearErrors: mailClearErrors,
    reset: mailReset,
    resetField: mailResetField
  } = useForm({
    resolver: yupResolver(mailSchema)
  });

  const currentDate = new Date();

  const documentOption = [
    { id: 'PDF', label: 'PDF' },
    { id: 'EXCEL', label: 'Excel' }
  ];

  const truckNumbersDrop =
    data?.length > 0
      ? data.map((value: string, index: number) => ({
          id: value,
          label: capitalizeFirstLetter(value)
        }))
      : [];

  let columns = [
    {
      field: 'vehicleType',
      headerName: `Vehicle Type`,
      flex: 1,
      minWidth: 150
    },
    {
      field: 'vehicleNumber',
      headerName: `Vehicle Number`,
      flex: 1,
      minWidth: 150
    },
    {
      field: 'part',
      headerName: `Equipment Maintenance`,
      flex: 1,
      minWidth: 180
    },
    {
      field: 'make',
      headerName: `Make`,
      flex: 1,
      minWidth: 150
    },
    {
      field: 'type',
      headerName: `Type`,
      flex: 1,
      minWidth: 200
    },
    {
      field: 'maintenanceDateFormat',
      headerName: `Maintenance Date`,
      flex: 1,
      minWidth: 150
    },
    {
      field: 'dueCriteria',
      headerName: ` Due Criteria`,
      flex: 1,
      minWidth: 170
    },
    {
      field: 'serviceInterval',
      headerName: `Service Interval`,
      flex: 1,
      minWidth: 170,
      renderCell: ({ row }: any) => {
        return String(row.serviceInterval).concat(' ', row.intervalType);
      }
    },
    {
      field: 'nextDueDateFormat',
      headerName: `Next Due date`,
      flex: 1,
      minWidth: 170
    },
    {
      field: 'currentOdometer',
      headerName: `Current Odometer`,
      flex: 1,
      minWidth: 150
    },
    {
      field: 'dueOdometerDistance',
      headerName: `Due Odometer Distance`,
      flex: 1,
      minWidth: 200
    }
  ];

  const handleClose = () => {};

  //function to handle Custom date options in From date field
  const handleCustomFrom = (start: Date, end: Date) => {
    if (start) {
      setToDateDisabled(false);
    }
    setValue('startDate', start);
    setGetStartDate(start);
    setValue('endDate', end);
    clearErrors('startDate');
    clearErrors('endDate');
    setGetEndDate(end);
  };

  //function to handle From datea
  const handleFromDate = (date: any) => {
    if (date === null) return;
    setGetStartDate(date?.$d);
    const fromDate: number | null = convertToEpoch2(date?.$d);
    setFromTimeStamp(fromDate);
    setCurrentTimeStamp(convertToEpoch(currentDate));
    setValue('startDate', date?.$d);
    clearErrors('startDate');
    clearErrors('endDate');
    setGetEndDate(new Date(date?.$d?.getTime() + 1000 * 600));
    setValue('endDate', new Date(date?.$d?.getTime() + 1000 * 600));
    if (date) {
      setToDateDisabled(false);
    }
    if (fromDate && currentTimestamp !== null && fromDate > currentTimestamp) {
      dispatch(
        updateToast({
          show: true,
          message: 'From date is Invalid',
          severity: 'error'
        })
      );
    }
  };

  // function to handle pagination
  const handlePagination = async (newPaginationModel: any) => {
    const { page, pageSize } = newPaginationModel;
    setPageNo(page + 1);
    let payloads = { ...payload, pageno: page + 1, pagesize: pageSize };
    setPayload(payloads);
    await dispatch(
      getMaintenance({ ...payloads, signal: createAbort().abortCall.signal })
    );
  };

  // sort
  const handleSortModelChange = async (model: any) => {
    setSortModel(model);
    const payloads = {
      deviceID: payload.deviceID,
      startDate: payload.startDate,
      endDate: payload.endDate,
      pageno: pageNo,
      pagesize: pageSize,
      sortBy: (model.length > 0 && model[0].sort.toUpperCase()) || null
    };
    setPayload(payloads);
    await dispatch(
      getMaintenance({ ...payloads, signal: createAbort().abortCall.signal })
    );
  };

  //function to handle Custom date options in To date field
  const handleCustomTo = (start: Date, end: Date) => {
    setValue('startDate', start);
    setGetStartDate(start);
    setValue('endDate', end);
    setGetEndDate(end);
    clearErrors('startDate');
    clearErrors('endDate');
  };

  //function to handle To date
  const handleToDate = (date: any) => {
    if (getStartDate === null) {
      setGetEndDate(date.$d);
      setValue('endDate', date?.$d);
      clearErrors('endDate');
      dispatch(
        updateToast({
          show: true,
          message: 'Please select "From Date"',
          severity: 'error'
        })
      );
    } else {
      setGetEndDate(date.$d);
      setValue('endDate', date?.$d);
      clearErrors('endDate');
    }
    const toDate: number | null = convertToEpoch2(date?.$d);
    setToTimeStamp(toDate);
    if (toDate && fromTimestamp != null && toDate < fromTimestamp) {
      dispatch(
        updateToast({
          show: true,
          message: 'To Date is invalid',
          severity: 'error'
        })
      );
      setValue('endDate', date?.$d);
      clearErrors('endDate');
    }
  };

  //function to get maintenance report details
  const handleGetReportClick: SubmitHandler<ReportData> = async data => {
    setIsDeviceDisabled(false);
    setIsDateDisabled(false);
    setPageNo(1);
    setPageSize(5);
    const payload = {
      deviceID: data.vehicle,
      startDate: convertToEpoch(data.startDate),
      endDate: convertToEpoch(data.endDate),
      pageno: 1,
      pagesize: 5
    };
    setPayload(payload);
    dispatch(getMaintenance({ ...payload, signal: createAbort().abortCall.signal }));
    reset();
    setGetStartDate(null);
    setGetEndDate(null);
    setToDateDisabled(true);
    setShowDataGrid(true);
  };

  //function to send Mail
  const sendMail = async (data: any) => {
    let payloads = data?.email
      ? {
          mail: data?.email,
          vehicleNumbers: payload?.deviceID === null ? [] : [payload?.deviceID],
          startDate: payload?.startDate,
          endDate: payload?.endDate,
          format: data?.document,
          sortBy: 'DESC'
        }
      : {
          vehicleNumbers: payload?.deviceID === null ? [] : [payload?.deviceID],
          startDate: payload?.startDate,
          endDate: payload?.endDate,
          format: data?.document,
          sortBy: 'DESC'
        };
    setNewMail(data?.email);
    dispatch(getPDFExcel(payloads));
    mailResetField('email');
  };

  useEffect(() => {
    if (maintenanceData?.length > 0) {
      setMaintenanceRows(maintenanceData);
    }
  }, [maintenanceData]);

  useEffect(() => {
    dispatch(truckNumbers(createAbort().abortCall.signal));
    return () => {
      dispatch(clearData());
      createAbort().abortCall.abort();
    };
  }, []);

  useEffect(() => {
    if (pdfData?.data?.message) {
      mailResetField('document');
      setDrawer(false);
      setNewMail(null);
    }
  }, [pdfData]);

  useEffect(() => {
    newMail ? setMailValues('email', newMail) : setMailValues('email', email);
  }, [email, newMail]);

  return (
    <>
      <Box className='maintenance'>
        <Grid
          container
          gap={1.2}
          component='form'
          position={'relative'}
          onSubmit={handleSubmit(handleGetReportClick)}
          columnSpacing={{ xs: 10, sm: 2, md: 3 }}
          alignItems={'center'}
          marginBottom={1.5}
        >
          <Grid
            item
            sm={5.5}
            xs={5.5}
            lg={2}
            className=' animate__animated animate__slideInRight'
          >
            <CustomSelect
              id='select-vehicle'
              options={truckNumbersDrop}
              control={control}
              name='vehicle'
              placeholder='Vehicle'
              className='reports-select'
              loading={maintenanceLoading}
            />
          </Grid>
          <Grid
            item
            sm={5.5}
            xs={5.5}
            lg={2}
            className=' animate__animated animate__slideInRight'
          >
            <CustomDateTimeCalendar
              id='select-startdate'
              name='startDate'
              control={control}
              disableFuture={true}
              disablePast={false}
              value={getStartDate}
              format='DD/MM/YYYY hh:mm a'
              placeholder='Start Date'
              getStartDate={getStartDate}
              isDisabled={isDateDisabled}
              className='reports-calendar'
              onDateSelect={(start: Date, end: Date) => handleCustomFrom(start, end)}
              onDateChange={(date: Date) => handleFromDate(date)}
              type='report'
            />
          </Grid>
          <Grid
            item
            sm={5.5}
            xs={5.5}
            lg={2}
            className=' animate__animated animate__slideInRight'
          >
            <CustomDateTimeCalendar
              id='select-enddate'
              name='endDate'
              control={control}
              placeholder='End Date'
              format='DD/MM/YYYY hh:mm a'
              disableFuture={true}
              disablePast={false}
              className='reports-calendar'
              getStartDate={getStartDate}
              value={getEndDate}
              onDateSelect={(start: Date, end: Date) => handleCustomTo(start, end)}
              onDateChange={(date: Date) => handleToDate(date)}
              minDateTime={
                getStartDate !== null ? dayjs(getStartDate?.getTime() + 1000 * 600) : null
              }
              maxDateTime={dayjs()
                .set('hour', new Date().getHours())
                .set('minutes', new Date().getMinutes())
                .startOf('minute')}
              isDisabled={toDateDisabled}
              type='report'
            />
          </Grid>
          <Grid
            item
            lg={3.5}
            md={3.5}
            sm={5.5}
            xs={5.5}
            className=' animate__animated animate__slideInRight'
          >
            <CustomButton className='get-report' category='Get Report' type='submit' />
          </Grid>
          <Grid
            className='maintenance-title animate__animated animate__slideInRight'
            item
            sm={5.5}
            xs={5.5}
            lg={2}
          >
            <CustomButton
              category='Add Maintenance'
              className='saveChanges'
              onClick={() => {
                setOpen(true);
                setRole('Add Maintenance');
              }}
            />
          </Grid>
        </Grid>
        {open && (
          <AddMaintenance
            open={open}
            setOpen={setOpen}
            roleType={role}
            filteredRow={filteredRow}
            setFilteredRow={setFilteredRow}
          />
        )}
        {isDelete && (
          <CustomDeletePopup
            open={isDelete}
            setOpen={setIsDelete}
            handleClose={handleClose}
            message={message}
          />
        )}
        {maintenanceData?.length > 0 && (
          <CustomDataGrid
            rows={maintenanceData}
            columns={columns}
            loading={loading}
            // onSortModelChange={handleSortModelChange}
            rowCount={count}
            type={'autoplanner'}
            // sortingMode='server'
            pageNo={pageNo}
            pageSize={pageSize}
            paginationModel={{
              page: pageNo ? pageNo - 1 : 0,
              pageSize: pageSize
            }}
            onPaginationModelChange={(value: any) => {
              handlePagination(value);
            }}
          />
        )}
        {drawer && (
          <Dialog open={drawer} component={'form'} onSubmit={mailSubmit(sendMail)}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: '5%',
                width: '500px'
              }}
            >
              <Typography sx={{ fontWeight: 700, paddingBottom: '3%' }}>
                Send Mail
              </Typography>

              <Box>
                <Box sx={{ marginBottom: '2%' }}>
                  <CustomTextField
                    control={mailControl}
                    name={'email'}
                    id={'e-mail'}
                    label='Email ID'
                    placeholder='Enter Email ID'
                  />
                </Box>

                <Box>
                  <CustomSelect
                    id={'document'}
                    options={documentOption}
                    control={mailControl}
                    name='document'
                    label='Document Type'
                    placeholder='Enter Document Type'
                  />
                </Box>
                <Box sx={{ marginTop: '5%', display: 'flex', gap: '15px' }}>
                  <CustomButton
                    className='saveChanges'
                    category={'Send Mail'}
                    type='submit'
                    loading={buttonLoading}
                  />
                  <CustomButton
                    className='cancel'
                    category='Cancel'
                    variant='outlined'
                    onClick={() => {
                      setDrawer(false);
                      mailReset();
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Dialog>
        )}
      </Box>
    </>
  );
};

export default Maintenance;
